import axios from 'axios';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase-config';
import webBackApi from '../api/webBackApi';
import { daily } from '../socialStudio/socialStudio.api';

// Marketing › Redes sociales. Instancia propia de axios (un 403 de este módulo no cierra la sesión).
// En local: REACT_APP_REDES_API_URL=http://localhost:8099 (backend simulado) y REACT_APP_REDES_DEV=1
// (material de muestra y subidas a la carpeta local, nunca a Firebase).
const BASE = (process.env.REACT_APP_REDES_API_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:8081').replace(/\/$/, '');
const DEV = process.env.NODE_ENV === 'development' && process.env.REACT_APP_REDES_DEV === '1';

const api = axios.create({ baseURL: `${BASE}/api/redes` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authLisualToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const errorMsg = (e) => e?.response?.data?.message || e?.message || 'Algo salió mal';
const d = (p) => p.then((r) => r.data);

export const redes = {
  estado: (customerId) => d(api.get('/estado', { params: { customerId } })),
  conectar: (red, customerId, volver) => d(api.post(`/conectar/${red}`, { customerId, volver })),
  desconectar: (id) => d(api.delete(`/cuentas/${encodeURIComponent(id)}`)),
  publicaciones: (customerId, desde, hasta) => d(api.get('/publicaciones', { params: { customerId, desde, hasta } })),
  crear: (body) => d(api.post('/publicaciones', body)),
  editar: (id, body) => d(api.put(`/publicaciones/${id}`, body)),
  cancelar: (id) => d(api.post(`/publicaciones/${id}/cancelar`)),
  reintentar: (id) => d(api.post(`/publicaciones/${id}/reintentar`)),
  borrar: (id) => d(api.delete(`/publicaciones/${id}`)),
};

/** Instagram sólo acepta JPEG: cualquier imagen se pasa a JPEG antes de subir. */
export async function aJpeg(blob) {
  if (blob.type === 'image/jpeg') return blob;
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise((ok, mal) => { const i = new Image(); i.onload = () => ok(i); i.onerror = mal; i.src = url; });
    const c = document.createElement('canvas');
    c.width = img.naturalWidth; c.height = img.naturalHeight;
    const ctx = c.getContext('2d'); ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height); ctx.drawImage(img, 0, 0);
    return await new Promise((r) => c.toBlob(r, 'image/jpeg', 0.92));
  } finally { URL.revokeObjectURL(url); }
}

/** Sube un archivo (pieza de la vidriera o de la PC) y devuelve un link público estable. */
export async function subir(blob, customerId, tipo, onProgress) {
  const archivo = tipo === 'foto' ? await aJpeg(blob) : blob;
  const ext = tipo === 'foto' ? 'jpg' : 'mp4';
  if (DEV) {
    const r = await fetch(`${BASE}/api/redes-dev/subir?ext=${ext}`, { method: 'POST', headers: { 'Content-Type': 'application/octet-stream' }, body: archivo });
    if (!r.ok) throw new Error('No se pudo subir el archivo al servidor local');
    return (await r.json()).url;
  }
  return new Promise((resolve, reject) => {
    const tarea = uploadBytesResumable(ref(storage, `redes/${customerId || 'sin-cliente'}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`), archivo, { contentType: tipo === 'foto' ? 'image/jpeg' : 'video/mp4' });
    tarea.on('state_changed', (s) => onProgress && onProgress(s.bytesTransferred / (s.totalBytes || 1)), reject, async () => resolve(await getDownloadURL(tarea.snapshot.ref)));
  });
}

const enAR = (d) => new Date(d).toLocaleDateString('es-AR', { day: 'numeric', month: 'short', timeZone: 'America/Argentina/Buenos_Aires' });

/** El último timelapse listo (el mismo que muestra Obra › Timelapse). Sólo se pide al abrir esa opción. */
export async function ultimoTimelapse(feedID) {
  if (DEV) {
    try { const j = await (await fetch(`${BASE}/api/redes-dev/material`)).json(); if (j.timelapse) return { url: j.timelapse.url, titulo: 'Último timelapse' }; } catch (e) { /* real */ }
  }
  if (!feedID) return null;
  try {
    const { data } = await webBackApi.get(`/api/video/latest/${feedID}`, { timeout: 20000 });
    return data?.status === 'READY' && data.videoUrl ? { url: data.videoUrl, titulo: 'Último timelapse' } : { pendiente: true };
  } catch (e) {
    return null;
  }
}

/**
 * Fotos de la obra para publicar, repartidas en el tiempo
 * (una por semana para atrás + la mejor de hoy). En local sin login usa el material de muestra.
 */
export async function material(feedID) {
  if (DEV) {
    try {
      const r = await fetch(`${BASE}/api/redes-dev/material`);
      const j = await r.json();
      if (j.fotos?.length) {
        return {
          fotos: j.fotos.map((f) => ({ url: f.url, fecha: f.fecha, etiqueta: f.fecha ? enAR(f.fecha) : '' })),
        };
      }
    } catch (e) { /* sigue con el material real */ }
  }
  const out = { fotos: [] };
  if (!feedID) return out;
  const hoy = new Date();
  const fechas = [0, 7, 14, 28, 56, 84, 112, 168].map((n) => { const x = new Date(hoy); x.setDate(x.getDate() - n); return x; });
  const listas = await Promise.all(fechas.map((f) => daily(feedID, f)));
  listas.forEach((lista) => {
    if (!lista.length) return;
    // la foto más cercana al mediodía (mejor luz)
    const it = lista.reduce((b, x) => (!b || Math.abs(((x.ts.getUTCHours() + 21) % 24) - 13) < Math.abs(((b.ts.getUTCHours() + 21) % 24) - 13) ? x : b), null);
    out.fotos.push({ url: it.url, fecha: it.ts.toISOString(), etiqueta: enAR(it.ts) });
  });
  out.fotos.sort((a, b) => (a.fecha < b.fecha ? -1 : 1));
  return out;
}
